import { useRef, useCallback, useEffect } from 'react'

interface UseAudioRecorderOptions {
  onAudioData: (audioData: ArrayBuffer) => void
  chunkDurationMs?: number
  sampleRate?: number
  captureType?: 'microphone' | 'browser'
}

export function useAudioRecorder({
  onAudioData,
  chunkDurationMs = 300,
  sampleRate = 16000,
  captureType = 'microphone',
}: UseAudioRecorderOptions) {
  const audioContextRef = useRef<AudioContext | null>(null)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const processorRef = useRef<ScriptProcessorNode | null>(null)
  const isRecordingRef = useRef(false)

  const startRecording = useCallback(async () => {
    try {
      // Initialize AudioContext
      audioContextRef.current = new AudioContext({
        sampleRate: 44100, // Browser default
      })

      // Get audio access based on capture type
      if (captureType === 'browser') {
        // Capture audio from browser tab (for YouTube, etc.)
        mediaStreamRef.current = await (navigator.mediaDevices as any).getDisplayMedia({
          audio: {
            sampleRate: 44100,
            channelCount: 1,
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
          },
          video: false,
        })
      } else {
        // Default: microphone
        mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({
          audio: {
            sampleRate: 44100,
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
          },
        })
      }

      // Create source
      if (!mediaStreamRef.current) {
        throw new Error('Failed to get media stream')
      }
      sourceRef.current = audioContextRef.current.createMediaStreamSource(
        mediaStreamRef.current
      )

      // Create script processor for raw audio access
      const bufferSize = 4096
      processorRef.current = audioContextRef.current.createScriptProcessor(
        bufferSize,
        1, // mono input
        1  // mono output
      )

      // Audio buffer for accumulating chunks
      let audioBuffer: Float32Array[] = []
      const samplesPerChunk = Math.floor((sampleRate * chunkDurationMs) / 1000)

      processorRef.current.onaudioprocess = (event) => {
        if (!isRecordingRef.current) return

        const inputData = event.inputBuffer.getChannelData(0)
        
        // Resample from 44100 to 16000
        const resampledData = resampleAudio(inputData, 44100, sampleRate)
        
        audioBuffer.push(resampledData)

        // Check if we have enough samples for a chunk
        let totalSamples = audioBuffer.reduce((sum, arr) => sum + arr.length, 0)
        
        while (totalSamples >= samplesPerChunk) {
          // Extract chunk
          const chunk = new Float32Array(samplesPerChunk)
          let chunkIndex = 0
          let bufferIndex = 0
          let sampleIndex = 0

          while (chunkIndex < samplesPerChunk && bufferIndex < audioBuffer.length) {
            const remaining = samplesPerChunk - chunkIndex
            const available = audioBuffer[bufferIndex].length - sampleIndex
            const toCopy = Math.min(remaining, available)

            chunk.set(
              audioBuffer[bufferIndex].slice(sampleIndex, sampleIndex + toCopy),
              chunkIndex
            )

            chunkIndex += toCopy
            sampleIndex += toCopy

            if (sampleIndex >= audioBuffer[bufferIndex].length) {
              bufferIndex++
              sampleIndex = 0
            }
          }

          // Remove used buffers
          audioBuffer = audioBuffer.slice(bufferIndex)
          if (audioBuffer.length > 0 && sampleIndex > 0) {
            audioBuffer[0] = audioBuffer[0].slice(sampleIndex)
          }

          // Convert to 16-bit PCM
          const pcmData = floatTo16BitPCM(chunk)
          onAudioData((pcmData as any).buffer)

          totalSamples = audioBuffer.reduce((sum, arr) => sum + arr.length, 0)
        }
      }

      // Connect nodes
      sourceRef.current.connect(processorRef.current)
      processorRef.current.connect(audioContextRef.current.destination)

      isRecordingRef.current = true
      console.log('Recording started')
    } catch (err) {
      console.error('Failed to start recording:', err)
      throw err
    }
  }, [onAudioData, sampleRate, chunkDurationMs])

  const stopRecording = useCallback(() => {
    isRecordingRef.current = false

    // Disconnect and cleanup
    if (processorRef.current) {
      processorRef.current.disconnect()
      processorRef.current = null
    }

    if (sourceRef.current) {
      sourceRef.current.disconnect()
      sourceRef.current = null
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop())
      mediaStreamRef.current = null
    }

    if (audioContextRef.current) {
      audioContextRef.current.close()
      audioContextRef.current = null
    }

    console.log('Recording stopped')
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRecording()
    }
  }, [stopRecording])

  return { startRecording, stopRecording }
}

// Resample audio from sourceRate to targetRate
function resampleAudio(
  audioData: Float32Array,
  sourceRate: number,
  targetRate: number
): Float32Array {
  if (sourceRate === targetRate) {
    return audioData
  }

  const ratio = sourceRate / targetRate
  const newLength = Math.floor(audioData.length / ratio)
  const result = new Float32Array(newLength)

  for (let i = 0; i < newLength; i++) {
    const sourceIndex = Math.floor(i * ratio)
    result[i] = audioData[sourceIndex]
  }

  return result
}

// Convert Float32 audio to 16-bit PCM
function floatTo16BitPCM(input: Float32Array): ArrayBuffer {
  const buffer = new ArrayBuffer(input.length * 2)
  const view = new DataView(buffer)

  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]))
    const intValue = s < 0 ? s * 0x8000 : s * 0x7fff
    view.setInt16(i * 2, intValue, true) // little-endian
  }

  return buffer
}

export default useAudioRecorder
